import { format } from "date-fns"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight } from "lucide-react"
import useUserEventSideSheet from "@/modules/calendar/stores/use-user-event-side-sheet"
import { useEffect, useRef, useState, useCallback } from "react"
import { cn } from "@/lib/utils"
import { useSidebar } from "@/components/ui/sidebar"
import { PRODUCT_TOUR_STEPS } from "@/modules/product-tour/constants/steps"

interface CustomToolbarProps {
  currentDate: Date
  goToCurrentWeek: () => void
  handleNavigation: (action: "PREV" | "NEXT") => void
}

export default function CustomToolbar({
  currentDate,
  goToCurrentWeek,
  handleNavigation,
}: CustomToolbarProps) {
  const monthYearTitle = format(currentDate, "MMMM yyyy")
  const { openSideSheet } = useUserEventSideSheet()
  const [isSticky, setIsSticky] = useState(false)
  const toolbarRef = useRef<HTMLDivElement>(null)
  const initialPositionRef = useRef<number | null>(null)
  const lastScrollY = useRef(window.scrollY)
  const { state } = useSidebar()
  const frameRef = useRef<number | null>(null)
  const navigatingRef = useRef(false)

  // Set the initial position once and keep it stable
  useEffect(() => {
    if (toolbarRef.current && initialPositionRef.current === null) {
      const toolbarPosition = toolbarRef.current.getBoundingClientRect()
      initialPositionRef.current = toolbarPosition.top + window.scrollY
    }
    // Verify if it should be sticky based on the current scroll position
    if (initialPositionRef.current !== null && window.scrollY > initialPositionRef.current) {
      setIsSticky(true)
    }
  }, [])

  useEffect(() => {
    const rootEl = document.documentElement
    if (state === "expanded") {
      rootEl.style.setProperty("--header-width", "calc(100% - 16rem)")
    } else {
      rootEl.style.setProperty("--header-width", "calc(100% - 72px)")
    }
  }, [state])

  const handleScroll = useCallback(() => {
    if (frameRef.current !== null) {
      return
    }

    // Use requestAnimationFrame to limit updates
    frameRef.current = requestAnimationFrame(() => {
      if (initialPositionRef.current !== null) {
        const currentScrollY = window.scrollY
        // Only update if there is a significant change in scroll position
        if (Math.abs(currentScrollY - lastScrollY.current) > 5) {
          const shouldBeSticky = currentScrollY > initialPositionRef.current
          if (shouldBeSticky !== isSticky) {
            setIsSticky(shouldBeSticky)
          }
          lastScrollY.current = currentScrollY
        }
      }
      frameRef.current = null
    })
  }, [isSticky])

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", handleScroll)
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }
    }
  }, [handleScroll])

  const handleOnCreateEvent = () => {
    openSideSheet({ type: "addition" })
  }

  const handlePrevWeek = () => {
    navigatingRef.current = true
    const currentStickyState = isSticky
    handleNavigation("PREV")
    // Ensure the state is maintained (we make it asynchronous so it happens after the render)
    setTimeout(() => {
      setIsSticky(currentStickyState)
      navigatingRef.current = false
    }, 50)
  }

  const handleNextWeek = () => {
    navigatingRef.current = true
    const currentStickyState = isSticky
    handleNavigation("NEXT")
    // Ensure the state is maintained (we make it asynchronous so it happens after the render)
    setTimeout(() => {
      setIsSticky(currentStickyState)
      navigatingRef.current = false
    }, 50)
  }

  const handleGoToCurrentWeek = () => {
    navigatingRef.current = true
    const currentStickyState = isSticky
    goToCurrentWeek()
    // Ensure the state is maintained (we make it asynchronous so it happens after the render)
    setTimeout(() => {
      setIsSticky(currentStickyState)
      navigatingRef.current = false
    }, 50)
  }

  return (
    <div
      ref={toolbarRef}
      data-sidebar-state={state}
      className={cn(
        "mb-2.5 flex items-center justify-between px-0 py-2.5",
        isSticky ? `calendar-toolbar-fixed top-0 bg-neutral-50 px-4 py-3` : "!w-full",
        isSticky && state === "expanded"
          ? "left-[16rem] w-[calc(100%-16rem)]"
          : "left-[72px] w-[calc(100%-72px)]",
      )}
    >
      <div className="text-[22px] font-bold capitalize">{monthYearTitle}</div>
      <div className="flex items-center gap-6">
        <Button onClick={handleOnCreateEvent} size="sm" id={PRODUCT_TOUR_STEPS.AGENDA_CREATE_EVENT}>
          + Crear evento
        </Button>
        <Button
          variant="link"
          className="px-2 py-0 text-foreground hover:bg-neutral-200 hover:no-underline"
          onClick={handleGoToCurrentWeek}
        >
          Ir a semana actual
        </Button>
        <div className="flex items-start justify-center gap-4">
          <Button className="bg-transparent p-0 text-gray-600" onClick={handlePrevWeek}>
            <ChevronLeft className="!size-6" />
          </Button>

          <Button className="bg-transparent p-0 text-gray-600" onClick={handleNextWeek}>
            <ChevronRight className="!size-6" />
          </Button>
        </div>
      </div>
    </div>
  )
}
